import { useEffect, useMemo, useState } from "react";
import { Download, Filter, RefreshCw, FileBarChart2, Camera } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { AuthImage } from "@/components/AuthImage";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage, getToken, API, formatTanggalID } from "@/lib/api";

const STATUS_OPTIONS = [
  "Ontime",
  "Terlambat",
  "Pulang Cepat",
  "Cuti",
  "Izin",
  "Sakit",
  "Alpha",
  "Dinas Luar",
  "Tugas Luar",
  "Istirahat Haid",
  "Istirahat Melahirkan / Gugur Kandungan",
];
const ANY = "__ANY__";

function toDateInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function AdminLaporan() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(toDateInput(first));
  const [endDate, setEndDate] = useState(toDateInput(today));
  const [unitId, setUnitId] = useState(ANY);
  const [lokasiId, setLokasiId] = useState(ANY);
  const [status, setStatus] = useState(ANY);
  const [units, setUnits] = useState([]);
  const [divisi, setDivisi] = useState([]);
  const [lokasi, setLokasi] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [view, setView] = useState("rincian"); // "rincian" | "rekap"
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [zoomFoto, setZoomFoto] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [u, l, dv] = await Promise.all([
          api.get("/master/unit"),
          api.get("/master/lokasi"),
          api.get("/master/divisi").catch(() => ({ data: [] })),
        ]);
        setUnits(u.data);
        setLokasi(l.data);
        setDivisi(dv.data);
      } catch (e) {
        toast.error(apiErrorMessage(e));
      }
    })();
  }, []);

  // Grup unit per divisi untuk memudahkan seleksi hirarkis (Divisi > Unit)
  const unitsGrouped = useMemo(() => {
    const dvMap = {};
    divisi.forEach((d) => { dvMap[d.id] = d.nama; });
    const groups = {};
    units.forEach((u) => {
      const key = dvMap[u.divisi_id] || "Lainnya";
      if (!groups[key]) groups[key] = [];
      groups[key].push(u);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([divisiNama, list]) => ({
        divisi: divisiNama,
        units: list.sort((a, b) => a.nama.localeCompare(b.nama)),
      }));
  }, [units, divisi]);

  const params = useMemo(
    () => ({
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      unit_id: unitId !== ANY ? unitId : undefined,
      lokasi_id: lokasiId !== ANY ? lokasiId : undefined,
      status: status !== ANY ? status : undefined,
    }),
    [startDate, endDate, unitId, lokasiId, status]
  );

  async function loadReport() {
    setLoading(true);
    try {
      if (view === "rekap") {
        const { data } = await api.get("/reports/attendance/summary", { params });
        setSummary(data.rows || []);
      } else {
        const { data } = await api.get("/reports/attendance", { params });
        setRows(data);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  async function doExport(kind) {
    setExporting(kind);
    try {
      const search = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") search.append(k, v);
      });
      const base = view === "rekap" ? "/reports/attendance/summary" : "/reports/attendance";
      const ext = kind === "csv" ? "export.csv" : "export";
      const url = `${API}${base}/${ext}?${search.toString()}`;
      const token = getToken();
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || "Gagal export");
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const prefix = view === "rekap" ? "rekap-absensi" : "laporan-absensi";
      a.download = `${prefix}-${startDate}-${endDate}.${kind === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Laporan berhasil diunduh");
    } catch (e) {
      toast.error(e.message || "Gagal export");
    } finally {
      setExporting(null);
    }
  }

  const stat = useMemo(() => {
    const total = rows.length;
    const ontime = rows.filter((r) => r.check_in_status === "Ontime").length;
    const terlambat = rows.filter((r) => r.check_in_status === "Terlambat").length;
    const pulangCepat = rows.filter((r) => r.check_out_status === "Pulang Cepat").length;
    return { total, ontime, terlambat, pulangCepat };
  }, [rows]);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="page-laporan">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Laporan Administrasi
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Rekap Absensi
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {isAdmin
              ? "Filter data absensi lintas unit & lokasi, lalu export ke Excel."
              : "Data absensi staf pada Unit Kerja Anda — unit lain otomatis tersembunyi."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={loadReport}
            disabled={loading}
            data-testid="report-refresh-btn"
            className="gap-2"
          >
            <RefreshCw size={14} /> Muat Ulang
          </Button>
          {isAdmin ? (
            <Button
              onClick={() => doExport("xlsx")}
              disabled={exporting !== null || rows.length === 0}
              className="gap-2 bg-teal-800 hover:bg-teal-900"
              data-testid="report-export-btn"
            >
              <Download size={14} /> {exporting === "xlsx" ? "Menyiapkan…" : "Export Excel"}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-slate-500 mb-4">
          <Filter size={12} /> Filter
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Dari</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1.5"
              data-testid="filter-start-date"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Sampai</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-1.5"
              data-testid="filter-end-date"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Unit Kerja</Label>
            {isAdmin ? (
              <Select value={unitId} onValueChange={setUnitId}>
                <SelectTrigger className="mt-1.5" data-testid="filter-unit">
                  <SelectValue placeholder="Semua Unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Semua Unit</SelectItem>
                  {unitsGrouped.map((grp) => (
                    <div key={grp.divisi}>
                      <div className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-teal-700 bg-slate-50 border-b border-slate-100">
                        {grp.divisi}
                      </div>
                      {grp.units.map((u) => (
                        <SelectItem key={u.id} value={u.id} data-testid={`filter-unit-opt-${u.id}`}>
                          {u.nama}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div
                className="mt-1.5 h-10 flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600"
                data-testid="filter-unit-locked"
              >
                {units.find((u) => u.id === user?.unit_id)?.nama || "Unit Kerja Anda"}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Lokasi</Label>
            <Select value={lokasiId} onValueChange={setLokasiId}>
              <SelectTrigger className="mt-1.5" data-testid="filter-lokasi">
                <SelectValue placeholder="Semua Lokasi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Semua Lokasi</SelectItem>
                {lokasi.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.nama}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1.5" data-testid="filter-status">
                <SelectValue placeholder="Semua Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Semua Status</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={loadReport} className="bg-teal-700 hover:bg-teal-800" data-testid="filter-apply-btn">
            Terapkan Filter
          </Button>
        </div>
      </div>

      {/* View toggle */}
      <div className="mt-6 flex items-center gap-2" data-testid="report-view-toggle">
        <button
          onClick={() => setView("rincian")}
          className={`px-4 py-2 rounded-md text-sm font-semibold border transition ${
            view === "rincian"
              ? "bg-teal-700 text-white border-teal-700"
              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          }`}
          data-testid="view-rincian-btn"
        >
          Rincian Harian
        </button>
        <button
          onClick={() => setView("rekap")}
          className={`px-4 py-2 rounded-md text-sm font-semibold border transition ${
            view === "rekap"
              ? "bg-teal-700 text-white border-teal-700"
              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          }`}
          data-testid="view-rekap-btn"
        >
          Rekapitulasi
        </button>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatBox label="Total Baris" value={stat.total} tone="slate" testId="stat-total" />
        <StatBox label="Ontime" value={stat.ontime} tone="emerald" testId="stat-ontime" />
        <StatBox label="Terlambat" value={stat.terlambat} tone="rose" testId="stat-terlambat" />
        <StatBox label="Pulang Cepat" value={stat.pulangCepat} tone="amber" testId="stat-pulang-cepat" />
      </div>

      {view === "rekap" ? (
        /* Rekapitulasi table */
        <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="rekap-table">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="sticky top-0">
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Pegawai</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Unit</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Hari</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-emerald-600 text-right">Ontime</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-rose-600 text-right">Terlambat</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-amber-600 text-right">Pulang Cepat</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-blue-600 text-right">Cuti</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-sky-600 text-right">Izin</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-orange-600 text-right">Sakit</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-purple-600 text-right">Dinas Luar</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-violet-600 text-right">Tugas Luar</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-pink-600 text-right">Haid</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-fuchsia-600 text-right">Melahirkan</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-600 text-right">Alpha</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-teal-600 text-right">WFA</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={15} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
                ) : summary.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} className="text-center py-14">
                      <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                        <FileBarChart2 size={22} />
                      </div>
                      <div className="mt-3 font-semibold text-slate-700">Belum ada data rekap</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  summary.map((r) => (
                    <TableRow key={r.user_id} data-testid={`rekap-row-${r.user_id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                      <TableCell>
                        <div className="font-semibold text-slate-900">{r.nama}</div>
                        <div className="text-xs text-slate-500 font-mono-clock">{r.npp}</div>
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">{r.unit_nama}</TableCell>
                      <TableCell className="text-right font-mono-clock text-slate-800">{r.total_hari}</TableCell>
                      <TableCell className="text-right font-mono-clock text-emerald-700 font-semibold">{r.ontime}</TableCell>
                      <TableCell className="text-right font-mono-clock text-rose-700 font-semibold">{r.terlambat}</TableCell>
                      <TableCell className="text-right font-mono-clock text-amber-700 font-semibold">{r.pulang_cepat}</TableCell>
                      <TableCell className="text-right font-mono-clock text-blue-700 font-semibold">{r.cuti}</TableCell>
                      <TableCell className="text-right font-mono-clock text-sky-700 font-semibold">{r.izin}</TableCell>
                      <TableCell className="text-right font-mono-clock text-orange-700 font-semibold">{r.sakit}</TableCell>
                      <TableCell className="text-right font-mono-clock text-purple-700 font-semibold">{r.dinas_luar}</TableCell>
                      <TableCell className="text-right font-mono-clock text-violet-700 font-semibold">{r.tugas_luar}</TableCell>
                      <TableCell className="text-right font-mono-clock text-pink-700 font-semibold">{r.istirahat_haid}</TableCell>
                      <TableCell className="text-right font-mono-clock text-fuchsia-700 font-semibold">{r.istirahat_melahirkan}</TableCell>
                      <TableCell className="text-right font-mono-clock text-slate-700 font-semibold">{r.alpha}</TableCell>
                      <TableCell className="text-right font-mono-clock text-teal-700 font-semibold">{r.wfa_hari}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : (
      /* Rincian Harian table (existing) */
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="sticky top-0">
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Tanggal</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">NPP</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Nama</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Unit</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Lokasi</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Masuk</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Pulang</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Status</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Detail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <FileBarChart2 size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Belum ada data untuk filter ini</div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} data-testid={`report-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <TableCell className="font-semibold text-slate-800 whitespace-nowrap">{r.tanggal_lokal}</TableCell>
                    <TableCell className="text-slate-700 font-mono-clock text-xs">{r.npp}</TableCell>
                    <TableCell className="text-slate-900 font-semibold">{r.nama}</TableCell>
                    <TableCell className="text-slate-600">{r.unit_nama}</TableCell>
                    <TableCell className="text-slate-600">{r.lokasi_nama}</TableCell>
                    <TableCell className="font-mono-clock text-slate-900">{r.check_in_lokal || "—"}</TableCell>
                    <TableCell className="font-mono-clock text-slate-900">{r.check_out_lokal || "—"}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <StatusBadge status={r.check_in_status} size="sm" />
                        {r.check_out_status ? <StatusBadge status={r.check_out_status} size="sm" /> : null}
                        {r.absence_status ? <StatusBadge status={r.absence_status} size="sm" /> : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <button
                        onClick={() => setDetail(r)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-teal-800 hover:text-teal-900"
                        data-testid={`report-detail-${r.id}`}
                      >
                        <Camera size={13} /> Detail
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      )}

      {/* Detail Absensi — foto Check-In / Check-Out */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="report-detail-dialog">
          <DialogHeader>
            <DialogTitle>Detail Absensi</DialogTitle>
            <DialogDescription>
              {detail ? `${detail.nama} — ${detail.npp} — ${formatTanggalID(detail.tanggal_lokal)}` : ""}
            </DialogDescription>
          </DialogHeader>
          {detail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Check-In */}
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Foto Check-In
                  </div>
                  <AuthImage
                    path={detail.check_in_selfie}
                    kind="selfie"
                    alt="Foto Check-In"
                    className="w-full h-48 rounded-lg border border-slate-200"
                    onClick={() => detail.check_in_selfie && setZoomFoto({ path: detail.check_in_selfie, label: "Foto Check-In" })}
                    testId="detail-foto-checkin"
                  />
                  <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                    <div>Jam: <span className="font-mono-clock text-slate-900">{detail.check_in_lokal || "—"}</span></div>
                    <div>Status: {detail.check_in_status ? <StatusBadge status={detail.check_in_status} size="sm" /> : "—"}</div>
                    {detail.check_in_jarak_meter != null ? (
                      <div>Jarak: <span className="font-mono-clock">{detail.check_in_jarak_meter} m</span></div>
                    ) : null}
                  </div>
                </div>
                {/* Check-Out */}
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Foto Check-Out
                  </div>
                  <AuthImage
                    path={detail.check_out_selfie}
                    kind="selfie"
                    alt="Foto Check-Out"
                    className="w-full h-48 rounded-lg border border-slate-200"
                    onClick={() => detail.check_out_selfie && setZoomFoto({ path: detail.check_out_selfie, label: "Foto Check-Out" })}
                    testId="detail-foto-checkout"
                  />
                  <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                    <div>Jam: <span className="font-mono-clock text-slate-900">{detail.check_out_lokal || "—"}</span></div>
                    <div>Status: {detail.check_out_status ? <StatusBadge status={detail.check_out_status} size="sm" /> : "—"}</div>
                    {detail.check_out_jarak_meter != null ? (
                      <div>Jarak: <span className="font-mono-clock">{detail.check_out_jarak_meter} m</span></div>
                    ) : null}
                  </div>
                </div>
              </div>
              {detail.absence_status ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <b>Status Absensi Otomatis:</b> {detail.absence_status}
                  {detail.benefit_deduction_status ? (
                    <span className="ml-2">• {detail.benefit_deduction_status}</span>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Foto zoom viewer */}
      <Dialog open={!!zoomFoto} onOpenChange={(v) => !v && setZoomFoto(null)}>
        <DialogContent className="max-w-3xl" data-testid="foto-zoom-dialog">
          <DialogHeader>
            <DialogTitle>{zoomFoto?.label || "Foto"}</DialogTitle>
          </DialogHeader>
          {zoomFoto ? (
            <AuthImage
              path={zoomFoto.path}
              kind="selfie"
              alt={zoomFoto.label}
              className="w-full max-h-[70vh] rounded-lg"
              testId="foto-zoom-image"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, tone, testId }) {
  const toneMap = {
    slate: "border-slate-200 text-slate-900",
    emerald: "border-emerald-200 text-emerald-800",
    rose: "border-rose-200 text-rose-800",
    amber: "border-amber-200 text-amber-800",
  };
  return (
    <div className={`rounded-xl border bg-white p-5 ${toneMap[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
        {label}
      </div>
      <div className="mt-1 font-mono-clock font-bold text-3xl leading-tight">{value}</div>
    </div>
  );
}
